import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    defaultOperationDate,
    FormDateField,
    FormSelect,
    FormTextarea,
    toOperationTimestamp,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { type CloseFishGroupRequest, CloseReason, useCloseFishGroup } from '@/domain/fishGroups';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
    fishGroupName: string;
    // Where the FG currently sits, shown as a reference next to the name in
    // the context line and used to compute the earliest selectable close
    // date. `null` when the FG has no active location (e.g. closed/discarded
    // already) — in that case the cage hint is hidden and the picker has
    // no lower bound.
    currentLocation?: { cageName: string; locationName: string; fromDate: string } | null;
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    reason: CloseReason | '';
    closedDate: Date | null;
    notes: string;
}

// A factory, not a constant: the date defaults to today, and a module-level
// constant would freeze "today" for the lifetime of the tab.
function makeDefaultValues(): FormValues {
    return {
        reason: '',
        closedDate: defaultOperationDate(),
        notes: '',
    };
}

const REASONS: CloseReason[] = [
    CloseReason.harvested,
    CloseReason.decommissioned,
    CloseReason.operational,
    CloseReason.other,
];

export default function CloseFishGroupModal(props: Props) {
    const { clientId, fishGroupId, fishGroupName, currentLocation, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: makeDefaultValues() });

    const handleClose = () => {
        if (isSubmitting) return;
        reset(makeDefaultValues());
        onClose();
    };

    const {
        close,
        error: serverError,
        reset: resetMutation,
    } = useCloseFishGroup(clientId, fishGroupId, {
        onSuccess: () => {
            ToastSuccess(t('actions.close.successToast'), 'fish-group-close-success');
            reset(makeDefaultValues());
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    // Bounds the picker (day granularity) and the submitted timestamp (instant
    // granularity) — the backend requires a close strictly after it.
    const locationFromDate = currentLocation ? new Date(currentLocation.fromDate) : null;

    const onSubmit = async (values: FormValues) => {
        if (!values.reason || !values.closedDate) return;
        const requestBody: CloseFishGroupRequest = {
            reason: values.reason,
            closedDate: toOperationTimestamp(values.closedDate, { notBefore: locationFromDate }),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        await close(requestBody).catch(() => {});
    };

    const reasonOptions = REASONS.map((reason) => ({
        value: reason,
        label: t(`actions.close.reasons.${reason}`),
    }));

    const body = (
        <>
            <p className={styles.contextLine}>
                {t('actions.close.context')}{' '}
                <span className={styles.contextStrong}>{fishGroupName}</span>
                {currentLocation && (
                    <span className={styles.contextMeta}>
                        {' · '}
                        {currentLocation.cageName} ({currentLocation.locationName})
                    </span>
                )}
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormSelect
                    control={control}
                    name="reason"
                    label={t('actions.close.fields.reason')}
                    placeholder={t('actions.close.fields.reasonPlaceholder')}
                    options={reasonOptions}
                    rules={{ required: true }}
                />
                <FormDateField
                    control={control}
                    name="closedDate"
                    label={t('actions.fields.closedDate')}
                    rules={{ required: true }}
                    blockFutureDates
                    minDate={locationFromDate}
                />
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" form={formId} disabled={isSubmitting}>
                {t('actions.close.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.close.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
